import { useState, useEffect } from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ExternalLink,
  Calendar,
  User,
  Info,
  ChevronDown,
  ChevronUp,
  Save,
  RefreshCw
} from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface LicenseConfirmationProps {
  studyPlanId: string;
  studyPlanTitle: string;
  onBack?: () => void;
}

// License types matching database enum
type LicenseType = 'unknown' | 'public_domain' | 'cc_license' | 'proprietary' | 'educational' | 'open_access' | 'subscription' | 'purchased' | 'fair_use';
type CCVariant = 'CC0' | 'CC_BY' | 'CC_BY_SA' | 'CC_BY_NC' | 'CC_BY_NC_SA' | 'CC_BY_ND' | 'CC_BY_NC_ND';

const LICENSE_TYPE_LABELS: Record<LicenseType, string> = {
  unknown: 'Ukjent',
  public_domain: 'Offentlig domene',
  cc_license: 'Creative Commons',
  proprietary: 'Proprietær',
  educational: 'Utdanningslisens',
  open_access: 'Åpen tilgang',
  subscription: 'Abonnement',
  purchased: 'Kjøpt lisens',
  fair_use: 'Sitatrett'
};

const CC_VARIANT_LABELS: Record<CCVariant, string> = {
  CC0: 'CC0 (Public Domain)',
  CC_BY: 'CC BY (Navngivelse)',
  CC_BY_SA: 'CC BY-SA (Navngivelse-DelPåSammeVilkår)',
  CC_BY_NC: 'CC BY-NC (Navngivelse-IkkeKommersiell)',
  CC_BY_NC_SA: 'CC BY-NC-SA (Navngivelse-IkkeKommersiell-DelPåSammeVilkår)',
  CC_BY_ND: 'CC BY-ND (Navngivelse-IngenBearbeidelser)',
  CC_BY_NC_ND: 'CC BY-NC-ND (Navngivelse-IkkeKommersiell-IngenBearbeidelser)'
};

interface Literature {
  id: string;
  title: string;
  original_url: string | null;
  literature_type: string;
  module_id: string | null;
  module_title?: string;
}

interface LicenseInfo {
  id: string;
  literature_id: string;
  license_type: LicenseType;
  cc_variant: CCVariant | null;
  license_url: string | null;
  rights_holder: string | null;
  allows_modification: boolean | null;
  allows_commercial_use: boolean | null;
  allows_distribution: boolean | null;
  requires_attribution: boolean | null;
  attribution_text: string | null;
  valid_from: string | null;
  valid_until: string | null;
  is_perpetual: boolean;
  usage_notes: string | null;
  verified_by: string | null;
  verified_at: string | null;
  verifier_email?: string;
}

interface LiteratureWithLicense extends Literature {
  license: LicenseInfo | null;
}

export default function LicenseConfirmation({ studyPlanId, studyPlanTitle, onBack }: LicenseConfirmationProps) {
  const [literatureItems, setLiteratureItems] = useState<LiteratureWithLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<LicenseInfo>>({});
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadLiterature();
  }, [studyPlanId]);

  async function loadLiterature() {
    setLoading(true);
    try {
      // Use backend API instead of direct Supabase calls
      const response = await authenticatedFetch(`/api/literature/study-plan/${studyPlanId}/licenses`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Kunne ikke laste lisensinformasjon');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Kunne ikke laste lisensinformasjon');
      }

      setLiteratureItems(data.items || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function getLicenseStatus(item: LiteratureWithLicense): 'verified' | 'warning' | 'unknown' | 'expired' {
    if (!item.license) return 'unknown';

    // Check if license is expired
    if (item.license.valid_until && !item.license.is_perpetual) {
      const expiryDate = new Date(item.license.valid_until);
      if (expiryDate < new Date()) return 'expired';
    }

    // Check if verified
    if (item.license.verified_by && item.license.verified_at) {
      return 'verified';
    }

    // Has license info but not verified
    if (item.license.license_type !== 'unknown') {
      return 'warning';
    }

    return 'unknown';
  }

  function getStatusIcon(status: string) {
    switch (status) {
      case 'verified':
        return <ShieldCheck className="text-green-600" size={20} />;
      case 'warning':
        return <ShieldAlert className="text-yellow-600" size={20} />;
      case 'expired':
        return <ShieldAlert className="text-red-600" size={20} />;
      default:
        return <ShieldQuestion className="text-gray-400" size={20} />;
    }
  }

  function startEditing(item: LiteratureWithLicense) {
    setEditingId(item.id);
    setEditForm(item.license ? {
      license_type: item.license.license_type,
      cc_variant: item.license.cc_variant,
      license_url: item.license.license_url,
      rights_holder: item.license.rights_holder,
      allows_modification: item.license.allows_modification,
      allows_commercial_use: item.license.allows_commercial_use,
      allows_distribution: item.license.allows_distribution,
      requires_attribution: item.license.requires_attribution,
      attribution_text: item.license.attribution_text,
      valid_until: item.license.valid_until,
      is_perpetual: item.license.is_perpetual,
      usage_notes: item.license.usage_notes
    } : {
      license_type: 'unknown' as LicenseType,
      is_perpetual: false
    });
    setExpandedId(item.id);
  }

  async function saveLicense(literatureId: string) {
    setSaving(true);
    try {
      const licenseData = {
        license_type: editForm.license_type || 'unknown',
        cc_variant: editForm.license_type === 'cc_license' ? editForm.cc_variant : null,
        license_url: editForm.license_url || null,
        rights_holder: editForm.rights_holder || null,
        allows_modification: editForm.allows_modification ?? null,
        allows_commercial_use: editForm.allows_commercial_use ?? null,
        allows_distribution: editForm.allows_distribution ?? null,
        requires_attribution: editForm.requires_attribution ?? null,
        attribution_text: editForm.attribution_text || null,
        valid_until: editForm.valid_until || null,
        is_perpetual: editForm.is_perpetual ?? false,
        usage_notes: editForm.usage_notes || null
      };

      // Use backend API instead of direct Supabase calls
      const response = await authenticatedFetch(`/api/literature/${literatureId}/license`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(licenseData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Kunne ikke lagre lisensinformasjon');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Kunne ikke lagre lisensinformasjon');
      }

      setSuccessMessage('Lisensinformasjon lagret');
      setTimeout(() => setSuccessMessage(null), 3000);

      setEditingId(null);
      setEditForm({});
      loadLiterature();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Calculate stats
  const stats = {
    total: literatureItems.length,
    verified: literatureItems.filter(l => getLicenseStatus(l) === 'verified').length,
    warning: literatureItems.filter(l => getLicenseStatus(l) === 'warning').length,
    unknown: literatureItems.filter(l => getLicenseStatus(l) === 'unknown').length,
    expired: literatureItems.filter(l => getLicenseStatus(l) === 'expired').length
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        {onBack && (
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800 mb-4 flex items-center gap-1"
          >
            &larr; Tilbake
          </button>
        )}
        <div className="flex items-center gap-3 mb-2">
          <Shield className="text-kf-primary" size={28} />
          <h1 className="text-2xl font-bold text-gray-900">Lisensbekreftelse</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Studieplan:</span>
          <span className="text-lg font-medium text-kf-primary">{studyPlanTitle}</span>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <Info className="text-kf-blue-600 flex-shrink-0" size={20} />
          <div className="text-sm text-kf-blue-800">
            <p className="font-medium mb-1">Om lisensbekreftelse</p>
            <p>
              For å sikre lovlig bruk av litteratur må alle kilder ha bekreftet lisensinformasjon.
              Bekreft at du har rettigheter til å bruke hver litteraturkilde i undervisningssammenheng.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-sm text-gray-500">Totalt</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-700">{stats.verified}</div>
          <div className="text-sm text-green-600">Bekreftet</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-700">{stats.warning}</div>
          <div className="text-sm text-yellow-600">Ubekreftet</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-600">{stats.unknown}</div>
          <div className="text-sm text-gray-500">Ukjent</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-700">{stats.expired}</div>
          <div className="text-sm text-red-600">Utløpt</div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-4 flex items-center gap-2">
          <CheckCircle size={18} />
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4 flex items-center gap-2">
          <AlertTriangle size={18} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <XCircle size={18} />
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <RefreshCw size={32} className="animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Laster litteratur...</p>
        </div>
      )}

      {/* Literature List */}
      {!loading && literatureItems.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Shield size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Ingen litteratur funnet for denne studieplanen</p>
        </div>
      )}

      {!loading && literatureItems.length > 0 && (
        <div className="space-y-4">
          {literatureItems.map(item => {
            const status = getLicenseStatus(item);
            const isExpanded = expandedId === item.id;
            const isEditing = editingId === item.id;

            return (
              <div
                key={item.id}
                className={`bg-white rounded-lg shadow border-l-4 ${
                  status === 'verified' ? 'border-green-500' :
                  status === 'expired' ? 'border-red-500' :
                  status === 'warning' ? 'border-yellow-500' :
                  'border-gray-300'
                }`}
              >
                {/* Header Row */}
                <div
                  className="p-4 flex items-center gap-4 cursor-pointer"
                  onClick={() => !isEditing && setExpandedId(isExpanded ? null : item.id)}
                >
                  {getStatusIcon(status)}

                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{item.title}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="capitalize">{item.literature_type}</span>
                      {item.module_title && (
                        <>
                          <span>&bull;</span>
                          <span>{item.module_title}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {item.license?.license_type && item.license.license_type !== 'unknown' && (
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                        {LICENSE_TYPE_LABELS[item.license.license_type]}
                      </span>
                    )}

                    {!isEditing && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(item);
                        }}
                        className="text-kf-primary hover:text-kf-secondary text-sm font-medium"
                      >
                        {item.license ? 'Rediger' : 'Bekreft lisens'}
                      </button>
                    )}

                    <button onClick={(e) => {
                      e.stopPropagation();
                      setExpandedId(isExpanded ? null : item.id);
                    }}>
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t pt-4">
                    {item.original_url && (
                      <div className="mb-4">
                        <a
                          href={item.original_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-kf-primary hover:underline flex items-center gap-1"
                        >
                          <ExternalLink size={14} />
                          {item.original_url}
                        </a>
                      </div>
                    )}

                    {isEditing ? (
                      /* Edit Form */
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Lisenstype *
                            </label>
                            <select
                              value={editForm.license_type || 'unknown'}
                              onChange={(e) => setEditForm({ ...editForm, license_type: e.target.value as LicenseType })}
                              className="w-full border rounded-lg px-3 py-2"
                            >
                              {Object.entries(LICENSE_TYPE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>

                          {editForm.license_type === 'cc_license' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                CC-variant
                              </label>
                              <select
                                value={editForm.cc_variant || ''}
                                onChange={(e) => setEditForm({ ...editForm, cc_variant: e.target.value as CCVariant })}
                                className="w-full border rounded-lg px-3 py-2"
                              >
                                <option value="">Velg variant</option>
                                {Object.entries(CC_VARIANT_LABELS).map(([value, label]) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Rettighetshaver
                            </label>
                            <input
                              type="text"
                              value={editForm.rights_holder || ''}
                              onChange={(e) => setEditForm({ ...editForm, rights_holder: e.target.value })}
                              placeholder="F.eks. Forfatter, Forlag, Organisasjon"
                              className="w-full border rounded-lg px-3 py-2"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Lisens-URL
                            </label>
                            <input
                              type="url"
                              value={editForm.license_url || ''}
                              onChange={(e) => setEditForm({ ...editForm, license_url: e.target.value })}
                              placeholder="https://..."
                              className="w-full border rounded-lg px-3 py-2"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tillatte bruksområder
                          </label>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editForm.allows_distribution ?? false}
                                onChange={(e) => setEditForm({ ...editForm, allows_distribution: e.target.checked })}
                                className="rounded"
                              />
                              Distribusjon
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editForm.allows_modification ?? false}
                                onChange={(e) => setEditForm({ ...editForm, allows_modification: e.target.checked })}
                                className="rounded"
                              />
                              Modifisering
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editForm.allows_commercial_use ?? false}
                                onChange={(e) => setEditForm({ ...editForm, allows_commercial_use: e.target.checked })}
                                className="rounded"
                              />
                              Kommersiell bruk
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editForm.requires_attribution ?? false}
                                onChange={(e) => setEditForm({ ...editForm, requires_attribution: e.target.checked })}
                                className="rounded"
                              />
                              Krever kreditering
                            </label>
                          </div>
                        </div>

                        {editForm.requires_attribution && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Krediteringstekst
                            </label>
                            <input
                              type="text"
                              value={editForm.attribution_text || ''}
                              onChange={(e) => setEditForm({ ...editForm, attribution_text: e.target.value })}
                              placeholder="Tekst som må vises ved bruk"
                              className="w-full border rounded-lg px-3 py-2"
                            />
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                              <input
                                type="checkbox"
                                checked={editForm.is_perpetual ?? false}
                                onChange={(e) => setEditForm({ ...editForm, is_perpetual: e.target.checked, valid_until: e.target.checked ? null : editForm.valid_until })}
                                className="rounded"
                              />
                              Evigvarende lisens
                            </label>
                          </div>

                          {!editForm.is_perpetual && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Utløpsdato
                              </label>
                              <input
                                type="date"
                                value={editForm.valid_until || ''}
                                onChange={(e) => setEditForm({ ...editForm, valid_until: e.target.value })}
                                className="w-full border rounded-lg px-3 py-2"
                              />
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Merknader
                          </label>
                          <textarea
                            value={editForm.usage_notes || ''}
                            onChange={(e) => setEditForm({ ...editForm, usage_notes: e.target.value })}
                            placeholder="Eventuelle spesielle vilkår eller merknader"
                            className="w-full border rounded-lg px-3 py-2"
                            rows={2}
                          />
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => saveLicense(item.id)}
                            disabled={saving}
                            className="bg-kf-primary text-white px-4 py-2 rounded-lg hover:bg-kf-secondary flex items-center gap-2 disabled:opacity-50"
                          >
                            <Save size={16} />
                            {saving ? 'Lagrer...' : 'Lagre og bekreft'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingId(null);
                              setEditForm({});
                            }}
                            className="text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100"
                          >
                            Avbryt
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div>
                        {item.license ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Lisenstype:</span>
                                <p className="font-medium">{LICENSE_TYPE_LABELS[item.license.license_type]}</p>
                              </div>
                              {item.license.cc_variant && (
                                <div>
                                  <span className="text-gray-500">CC-variant:</span>
                                  <p className="font-medium">{CC_VARIANT_LABELS[item.license.cc_variant]}</p>
                                </div>
                              )}
                              {item.license.rights_holder && (
                                <div>
                                  <span className="text-gray-500">Rettighetshaver:</span>
                                  <p className="font-medium">{item.license.rights_holder}</p>
                                </div>
                              )}
                              <div>
                                <span className="text-gray-500">Gyldighet:</span>
                                <p className="font-medium">
                                  {item.license.is_perpetual ? 'Evigvarende' :
                                   item.license.valid_until ? `Til ${new Date(item.license.valid_until).toLocaleDateString('nb-NO')}` : 'Ikke angitt'}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {item.license.allows_distribution && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Distribusjon</span>
                              )}
                              {item.license.allows_modification && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Modifisering</span>
                              )}
                              {item.license.allows_commercial_use && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Kommersiell bruk</span>
                              )}
                              {item.license.requires_attribution && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">Krever kreditering</span>
                              )}
                            </div>

                            {item.license.attribution_text && (
                              <div className="text-sm">
                                <span className="text-gray-500">Kreditering:</span>
                                <p className="italic">{item.license.attribution_text}</p>
                              </div>
                            )}

                            {item.license.usage_notes && (
                              <div className="text-sm bg-gray-50 p-2 rounded">
                                <span className="text-gray-500">Merknader:</span>
                                <p>{item.license.usage_notes}</p>
                              </div>
                            )}

                            {item.license.verified_at && (
                              <div className="text-xs text-gray-500 flex items-center gap-2 pt-2 border-t">
                                <User size={14} />
                                Bekreftet av {item.license.verifier_email} &bull;
                                <Calendar size={14} />
                                {new Date(item.license.verified_at).toLocaleDateString('nb-NO')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500">
                            <ShieldQuestion size={32} className="mx-auto mb-2 text-gray-300" />
                            <p>Ingen lisensinformasjon registrert</p>
                            <button
                              onClick={() => startEditing(item)}
                              className="mt-2 text-kf-primary hover:underline text-sm"
                            >
                              Legg til lisensinformasjon
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
